import * as THREE from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import discUrl from "~/assets/disc.png";
import type { OutputPayload, Style, VectorFn } from "~/lib/types";

/** Guards against a stray delta/point-count locking up the tab. */
const MAX_POINTS = 200_000;

export interface SceneHandle {
  /** Rebuilds the drawn objects from the graph's Output nodes. */
  setOutputs: (outputs: Record<string, OutputPayload>) => void;
  dispose: () => void;
}

/** Points from the last render, for the "Get Points (json)" export. */
let lastPoints: Record<string, { x: number; y: number; z: number }[]> = {};

export function getGeneratedPoints(): Record<
  string,
  { x: number; y: number; z: number }[]
> {
  return lastPoints;
}

export function createScene(container: HTMLElement): SceneHandle {
  const renderer = new THREE.WebGLRenderer({ antialias: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  container.appendChild(renderer.domElement);
  renderer.domElement.classList.add("block", "size-full");

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(75, 1, 0.1, 1000);
  camera.position.set(70, 70, 70);
  camera.lookAt(0, 0, 0);

  const controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;

  scene.add(new THREE.AmbientLight(0xffffff, 1.5));

  // Intensities are in candela since three r155 dropped legacy lighting.
  const key = new THREE.PointLight(0xffffff, 20000, 0, 2);
  key.position.set(50, 50, 50);
  scene.add(key);

  const fill = new THREE.PointLight(0xffffff, 40000, 0, 2);
  fill.position.set(-200, -200, -200);
  scene.add(fill);

  const sprite = new THREE.TextureLoader().load(discUrl);
  sprite.colorSpace = THREE.SRGBColorSpace;

  /** Everything generated from the graph lives here so it can be cleared wholesale. */
  const content = new THREE.Group();
  scene.add(content);

  /** Shader materials exposing a `frame` uniform, ticked once per rendered frame. */
  let animated: THREE.ShaderMaterial[] = [];

  const resize = (): void => {
    const width = Math.max(1, container.clientWidth);
    const height = Math.max(1, container.clientHeight);

    renderer.setSize(width, height, false);
    camera.aspect = width / height;
    camera.updateProjectionMatrix();
  };

  resize();
  const observer = new ResizeObserver(resize);
  observer.observe(container);

  let frame = 0;
  let running = true;

  const tick = (): void => {
    if (!running) return;
    requestAnimationFrame(tick);

    frame += 1;
    for (const material of animated) material.uniforms["frame"]!.value = frame;

    controls.update();
    renderer.render(scene, camera);
  };

  tick();

  return {
    setOutputs(outputs) {
      clearGroup(content);
      animated = [];

      const points: Record<string, { x: number; y: number; z: number }[]> = {};

      for (const [id, output] of Object.entries(outputs)) {
        const curve = generatePoints(output);
        points[id] = curve.map((point) => ({
          x: point.x,
          y: point.y,
          z: point.z,
        }));
        if (curve.length === 0) continue;

        const object = createObject(curve, output.style, sprite, animated);

        const rotation = output.transform?.rotation;
        if (rotation) {
          object.rotateX(rotation[0] || 0);
          object.rotateY(rotation[1] || 0);
          object.rotateZ(rotation[2] || 0);
        }

        content.add(object);
      }

      lastPoints = points;
    },

    dispose() {
      running = false;
      observer.disconnect();
      clearGroup(content);
      controls.dispose();
      sprite.dispose();
      renderer.dispose();
      renderer.domElement.remove();
    },
  };
}

/**
 * Releases GPU resources before dropping the objects.
 */
function clearGroup(group: THREE.Group): void {
  for (const child of [...group.children]) {
    group.remove(child);

    child.traverse((node) => {
      if (
        !(node instanceof THREE.Mesh) &&
        !(node instanceof THREE.Line) &&
        !(node instanceof THREE.Points)
      ) {
        return;
      }

      node.geometry.dispose();
      for (const material of Array.isArray(node.material)
        ? node.material
        : [node.material]) {
        material.dispose();
      }
    });
  }
}

/** Samples the curve functions across `nops` steps of size `delta`. */
function generatePoints(output: OutputPayload): THREE.Vector3[] {
  const vector: VectorFn | undefined = output.vector;
  if (!vector) return [];

  const initial = Number.isFinite(output.initial) ? output.initial : 0;
  const requested = Number.isFinite(output.nops)
    ? Math.floor(output.nops)
    : 2000;
  const count = Math.max(0, Math.min(requested, MAX_POINTS));
  const delta =
    output.delta === 0 || !Number.isFinite(output.delta)
      ? 0.00001
      : output.delta;

  const points: THREE.Vector3[] = [];

  for (let i = 0; i <= count; i += 1) {
    const t = initial + i * delta;
    const x = vector.x(t);
    const y = vector.y(t);
    const z = vector.z(t);

    // A single NaN poisons the whole buffer geometry, so drop bad samples.
    if (Number.isFinite(x) && Number.isFinite(y) && Number.isFinite(z)) {
      points.push(new THREE.Vector3(x, y, z));
    }
  }

  return points;
}

function createObject(
  points: THREE.Vector3[],
  style: Style | undefined,
  sprite: THREE.Texture,
  animated: THREE.ShaderMaterial[],
): THREE.Object3D {
  switch (style?.type) {
    case "index":
      return createIndexedSolids(points);
    case "extrusion":
      return createExtrusion(points, style, animated);
    case "points":
      return createPoints(points, style, sprite);
    default:
      return createLine(points, style, animated);
  }
}

function createLine(
  points: THREE.Vector3[],
  style: Style | undefined,
  animated: THREE.ShaderMaterial[],
): THREE.Object3D {
  const material =
    style?.type === "shader"
      ? trackShader(createFragmentMaterial(style.fragment), animated)
      : style?.type === "solid"
        ? solidMaterial(style.color)
        : trackShader(createPositionalMaterial(points), animated);

  const geometry = new THREE.BufferGeometry().setFromPoints(points);
  const line = new THREE.Line(geometry, material);
  line.name = "Lines";

  return line;
}

function solidMaterial(color: {
  r: number;
  g: number;
  b: number;
}): THREE.LineBasicMaterial {
  const material = new THREE.LineBasicMaterial();
  material.color.setRGB(
    color.r / 255,
    color.g / 255,
    color.b / 255,
    THREE.SRGBColorSpace,
  );
  return material;
}

/** One instanced mesh colored by index, instead of a mesh + material per point. */
function createIndexedSolids(points: THREE.Vector3[]): THREE.Object3D {
  const geometry = new THREE.IcosahedronGeometry(0.5);
  const material = new THREE.MeshBasicMaterial();
  const mesh = new THREE.InstancedMesh(geometry, material, points.length);
  mesh.name = "Lines";

  const matrix = new THREE.Matrix4();
  const color = new THREE.Color();

  points.forEach((point, index) => {
    mesh.setMatrixAt(index, matrix.setPosition(point));
    mesh.setColorAt(index, color.setHSL(index / points.length, 1, 0.5));
  });

  mesh.instanceMatrix.needsUpdate = true;
  if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;

  return mesh;
}

function createExtrusion(
  points: THREE.Vector3[],
  style: Extract<Style, { type: "extrusion" }>,
  animated: THREE.ShaderMaterial[],
): THREE.Object3D {
  // A curve needs at least two samples before it can be swept.
  if (points.length < 2) return createLine(points, undefined, animated);

  const { radius = 10, segments = 20, steps, color, styleInput } = style;
  const spline = new THREE.CatmullRomCurve3(points, false, "catmullrom");

  const shape = new THREE.Shape(createCircleProfile(radius, segments));
  const geometry = new THREE.ExtrudeGeometry(shape, {
    steps: steps > 0 ? Math.floor(steps) : points.length,
    bevelEnabled: false,
    extrudePath: spline,
  });

  const material =
    styleInput?.type === "shader"
      ? trackShader(createFragmentMaterial(styleInput.fragment), animated)
      : color
        ? new THREE.MeshStandardMaterial({
            color: new THREE.Color().setRGB(
              color.rgb.r / 255,
              color.rgb.g / 255,
              color.rgb.b / 255,
              THREE.SRGBColorSpace,
            ),
            metalness: 0.7,
            roughness: 0.3,
          })
        : trackShader(createPositionalMaterial(points), animated);

  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = "Lines";

  return mesh;
}

function createCircleProfile(
  radius: number,
  segments: number,
): THREE.Vector2[] {
  const count = Math.max(2, Math.floor(segments));

  return Array.from({ length: count }, (_, index) => {
    const angle = ((2 * index) / count) * Math.PI;
    return new THREE.Vector2(
      Math.cos(angle) * radius,
      Math.sin(angle) * radius,
    );
  });
}

function createPoints(
  points: THREE.Vector3[],
  style: Extract<Style, { type: "points" }>,
  sprite: THREE.Texture,
): THREE.Object3D {
  const positions = new Float32Array(points.length * 3);
  points.forEach((point, index) => {
    positions[index * 3] = point.x;
    positions[index * 3 + 1] = point.y;
    positions[index * 3 + 2] = point.z;
  });

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));

  const { color, size } = style;
  const material = new THREE.PointsMaterial({
    color: new THREE.Color().setRGB(
      color.rgb.r / 255,
      color.rgb.g / 255,
      color.rgb.b / 255,
      THREE.SRGBColorSpace,
    ),
    map: size > 0 ? sprite : null,
    size: size > 0 ? size : 1,
    alphaTest: 0.5,
    transparent: true,
  });

  const cloud = new THREE.Points(geometry, material);
  cloud.name = "Lines";

  return cloud;
}

const VERTEX_SHADER = `
varying vec3 _position;
varying vec3 _normal;
varying vec2 _uv;

void main() {
  _position = position;
  _normal = normal;
  _uv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

function createFragmentMaterial(fragment: string): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    vertexShader: VERTEX_SHADER,
    fragmentShader: fragment,
    uniforms: { frame: { value: 0 } },
  });
}

/** Default look: maps each axis of the curve's bounding box onto a color channel. */
function createPositionalMaterial(
  points: THREE.Vector3[],
): THREE.ShaderMaterial {
  const box = new THREE.Box3().setFromPoints(points);

  // Nudged so a flat axis doesn't divide by zero.
  const epsilon = 0.0001;
  const span = new THREE.Vector3()
    .subVectors(box.max, box.min)
    .addScalar(epsilon);

  return new THREE.ShaderMaterial({
    vertexShader: VERTEX_SHADER,
    fragmentShader: `
varying vec3 _position;
uniform float frame;
uniform vec3 boxMin;
uniform vec3 boxSpan;

void main() {
  gl_FragColor = vec4(0.2 + 0.8 * (_position - boxMin) / boxSpan, 1.0);
}
`,
    uniforms: {
      frame: { value: 0 },
      boxMin: { value: box.min.clone() },
      boxSpan: { value: span },
    },
  });
}

function trackShader(
  material: THREE.ShaderMaterial,
  animated: THREE.ShaderMaterial[],
): THREE.ShaderMaterial {
  animated.push(material);
  return material;
}
